import Button from '@amzn/awsui-components-core/button';
import ButtonDropdown from '@amzn/awsui-components-core/button-dropdown';
import Input from '@amzn/awsui-components-core/input';
import Select from '@amzn/awsui-components-core/select';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import React from 'react';
import {
  COLLAPSE_ALL_ID,
  EXPAND_ALL_ID,
  KEEP_MENU_OPS,
  SUBSET_VIEW_NONE,
  SYSTEM_NAME_ALIAS,
  SYSTEM_NAME_LABEL
} from 'src/components/star-grid/memberSubsetConstants';
import type { PnlHierarchyOption, PnlSubsetViewSummary } from 'src/components/star-grid/starGridTypes';
import { MEMBER_SUBSET_OP_LABELS, MemberSubsetOp } from 'src/components/star-grid/utils/subset-editor/memberSubset';
import { optionValue } from 'src/components/star-grid/utils/subset-editor/memberSubsetEditor';
import styles from './styles/MemberSubsetModal.module.scss';

interface Props {
  readonly toolbarLeading?: React.ReactNode;
  readonly hierarchies?: readonly PnlHierarchyOption[];
  readonly picked: PnlHierarchyOption | null;
  readonly onHierarchyChange: (value: string) => void;
  /** Saved views, aliases and Save all address a (dimension, hierarchy) pair. */
  readonly hasPair: boolean;
  readonly views: readonly PnlSubsetViewSummary[];
  readonly viewId: number | null;
  readonly onViewChange: (value: string | undefined) => void;
  readonly effectiveAlias: string | null;
  readonly aliasNames: readonly string[];
  readonly aliasNamesLoading: boolean;
  readonly onAliasChange: (alias: string | null) => void;
  readonly saveDisabled: boolean;
  readonly onSave: () => void;
  readonly onSaveAs: () => void;
  readonly search: string;
  readonly onSearchChange: (search: string) => void;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
  readonly resetDisabled: boolean;
  readonly onResetView: () => void;
  readonly onShowFlat: () => void;
  readonly onShowAllMembers: () => void;
  readonly onRemoveAll: () => void;
  /** Nothing ticked: the selection-driven Keep and Remove actions have no target. */
  readonly selectionEmpty: boolean;
  readonly onKeep: (op: MemberSubsetOp) => void;
  readonly onExpandAll: () => void;
  readonly onCollapseAll: () => void;
  readonly onRemoveElements: () => void;
  readonly onRemoveWithDescendants: () => void;
  readonly multiple: boolean;
  readonly onSelectAll: () => void;
  readonly onClearSelected: () => void;
  readonly testId?: string;
}

/** Two rows, as Prophecy lays them out: WHAT is being looked at on the first, what to DO to it on the second. */
export const MemberSubsetToolbar: React.FC<Props> = ({
  toolbarLeading,
  hierarchies,
  picked,
  onHierarchyChange,
  hasPair,
  views,
  viewId,
  onViewChange,
  effectiveAlias,
  aliasNames,
  aliasNamesLoading,
  onAliasChange,
  saveDisabled,
  onSave,
  onSaveAs,
  search,
  onSearchChange,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  resetDisabled,
  onResetView,
  onShowFlat,
  onShowAllMembers,
  onRemoveAll,
  selectionEmpty,
  onKeep,
  onExpandAll,
  onCollapseAll,
  onRemoveElements,
  onRemoveWithDescendants,
  multiple,
  onSelectAll,
  onClearSelected,
  testId
}) => (
  <div className={styles.toolbarRows}>
    <div className={styles.toolbar}>
      <SpaceBetween direction="horizontal" size="xs">
        {toolbarLeading}
        {/* Hidden when the dimension offers only one hierarchy: a one-option dropdown is noise. */}
        {(hierarchies?.length ?? 0) > 1 && (
          <Select
            selectedOption={picked === null ? null : { value: optionValue(picked), label: picked.label }}
            options={(hierarchies ?? []).map((option) => ({ value: optionValue(option), label: option.label }))}
            onChange={({ detail }) => onHierarchyChange(detail.selectedOption.value as string)}
            placeholder="Hierarchy"
            data-testid={testId ? `${testId}-hierarchy` : undefined}
          />
        )}
        {hasPair && (
          <Select
            selectedOption={
              viewId === null
                ? { value: '', label: SUBSET_VIEW_NONE }
                : { value: String(viewId), label: views.find((view) => view.id === viewId)?.view_name ?? '' }
            }
            options={[{ value: '', label: SUBSET_VIEW_NONE }, ...views.map((view) => ({ value: String(view.id), label: view.view_name }))]}
            onChange={({ detail }) => onViewChange(detail.selectedOption.value)}
            empty="No saved views"
            data-testid={testId ? `${testId}-view` : undefined}
          />
        )}
        {/* Always shown once the pair is known, so "no aliases" and "still loading" look different. */}
        {hasPair && (
          <Select
            selectedOption={
              effectiveAlias === null
                ? null
                : { value: effectiveAlias, label: effectiveAlias === SYSTEM_NAME_ALIAS ? SYSTEM_NAME_LABEL : effectiveAlias }
            }
            options={[{ value: SYSTEM_NAME_ALIAS, label: SYSTEM_NAME_LABEL }, ...aliasNames.map((name) => ({ value: name, label: name }))]}
            onChange={({ detail }) => onAliasChange(detail.selectedOption.value ?? null)}
            placeholder="Alias"
            disabled={aliasNamesLoading}
            statusType={aliasNamesLoading ? 'loading' : 'finished'}
            loadingText="Loading aliases…"
            empty="No aliases"
            data-testid={testId ? `${testId}-alias` : undefined}
          />
        )}
        {/* Gated on the pair too: without one there is nowhere to save to. */}
        {hasPair && (
          <ButtonDropdown
            mainAction={{ text: 'Save', onClick: onSave, disabled: saveDisabled }}
            disabled={saveDisabled}
            items={[{ id: 'saveAs', text: 'Save As' }]}
            onItemClick={onSaveAs}
            data-testid={testId ? `${testId}-save` : undefined}
          />
        )}
      </SpaceBetween>
      <div className={styles.toolbarSearch}>
        <Input
          type="search"
          value={search}
          onChange={({ detail }) => onSearchChange(detail.value)}
          placeholder="Search"
          ariaLabel="Search"
          data-testid={testId ? `${testId}-search` : undefined}
        />
      </div>
    </div>

    <div className={styles.toolbar}>
      <SpaceBetween direction="horizontal" size="xs">
        <Button iconName="undo" ariaLabel="Undo" disabled={!canUndo} onClick={onUndo} data-testid={testId ? `${testId}-undo` : undefined} />
        <Button iconName="redo" ariaLabel="Redo" disabled={!canRedo} onClick={onRedo} data-testid={testId ? `${testId}-redo` : undefined} />
        {/* A toolbar control rather than a footer link, so it stays reachable when the grid is emptied. */}
        <Button onClick={onResetView} disabled={resetDisabled} data-testid={testId ? `${testId}-reset-view` : undefined}>
          Reset View
        </Button>
        <ButtonDropdown
          mainAction={{ text: 'All', onClick: onShowFlat }}
          items={[
            { id: 'showAllMembers', text: 'Show All Members' },
            { id: 'removeAll', text: 'Remove All' }
          ]}
          onItemClick={({ detail }) => (detail.id === 'showAllMembers' ? onShowAllMembers() : onRemoveAll())}
          data-testid={testId ? `${testId}-all` : undefined}
        />
        {/* The dropdown stays open with nothing ticked: Expand/Collapse All Elements act on the whole view. */}
        <ButtonDropdown
          mainAction={{ text: 'Keep', onClick: () => onKeep('keep'), disabled: selectionEmpty }}
          items={[
            ...KEEP_MENU_OPS.map((op) => ({ id: op, text: MEMBER_SUBSET_OP_LABELS[op], disabled: selectionEmpty })),
            { id: EXPAND_ALL_ID, text: 'Expand All Elements' },
            { id: COLLAPSE_ALL_ID, text: 'Collapse All Elements' }
          ]}
          onItemClick={({ detail }) => {
            if (detail.id === EXPAND_ALL_ID) {
              onExpandAll();
              return;
            }
            if (detail.id === COLLAPSE_ALL_ID) {
              onCollapseAll();
              return;
            }
            onKeep(detail.id as MemberSubsetOp);
          }}
          data-testid={testId ? `${testId}-keep` : undefined}
        />
        {/* No main action: removal is always one of the two variants, so the click opens the choice. */}
        <ButtonDropdown
          disabled={selectionEmpty}
          items={[
            { id: 'remove', text: 'Remove Elements' },
            { id: 'removeDescendants', text: 'Remove Elements + Descendants' }
          ]}
          onItemClick={({ detail }) => (detail.id === 'removeDescendants' ? onRemoveWithDescendants() : onRemoveElements())}
          data-testid={testId ? `${testId}-remove` : undefined}
        >
          Remove
        </ButtonDropdown>
        {/* Hidden in single-select mode, where selecting everything could never satisfy OK. */}
        {multiple && (
          <Button onClick={onSelectAll} data-testid={testId ? `${testId}-select-all` : undefined}>
            Select All
          </Button>
        )}
        <Button onClick={onClearSelected} data-testid={testId ? `${testId}-clear` : undefined}>
          Clear Selected
        </Button>
      </SpaceBetween>
    </div>
  </div>
);
